import React from 'react';
import LgmButton from './LgmButton';
import { FdaSourceKey } from '../services/api/domains/products/types';
import { GRAY_500 } from 'theme/grayScale';

export type FdaSourceFilter = 'all' | FdaSourceKey;

export const FDA_SOURCE_FILTER_LABELS: Record<FdaSourceKey, string> = {
  ob: 'Orange Book',
  ndc: 'NDC',
  dmf: 'USDMF',
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export interface FdaSourceFilterControlsProps {
  sourceFilter: FdaSourceFilter;
  pageSize: number;
  onSourceFilterChange: (next: FdaSourceFilter) => void;
  onPageSizeChange: (next: number) => void;
}

/**
 * FDA Sources toolbar controls — the per-source filter (All/Orange
 * Book/NDC/USDMF) and the shared "Rows per page" control. Rendered in the
 * Products page's toolbar in place of the Filter/Grouping controls while
 * FDA Sources mode is active (docs/features/fda-source-product-search.md,
 * Task FSS-008; toolbar placement per 2026-09-23 follow-up feedback) —
 * extracted out of `FdaSourceSearchResults` so the toolbar (owned by
 * `ProductsRowGroupingDataGrid`) and the results columns (owned by
 * `FdaSourceSearchResults`) can share this state without either owning the
 * other's rendering.
 */
const FdaSourceFilterControls = ({
  sourceFilter,
  pageSize,
  onSourceFilterChange,
  onPageSizeChange,
}: FdaSourceFilterControlsProps) => (
  <div style={styles.controlsRow}>
    <div role="group" aria-label="FDA source filter" style={styles.filterGroup}>
      {(['all', 'ob', 'ndc', 'dmf'] as FdaSourceFilter[]).map(key => (
        <LgmButton
          key={key}
          type="button"
          variant={sourceFilter === key ? 'contained' : 'outlined'}
          color="primary"
          aria-pressed={sourceFilter === key}
          style={styles.filterButton}
          onClick={() => onSourceFilterChange(key)}>
          {key === 'all' ? 'All' : FDA_SOURCE_FILTER_LABELS[key]}
        </LgmButton>
      ))}
    </div>

    <label style={styles.pageSizeLabel}>
      Rows per page:{' '}
      <select
        value={pageSize}
        onChange={e => onPageSizeChange(Number(e.target.value))}
        style={styles.pageSizeSelect}
        aria-label="Rows per page">
        {PAGE_SIZE_OPTIONS.map(option => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  </div>
);

const styles: Record<string, React.CSSProperties> = {
  controlsRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  filterGroup: {
    display: 'flex',
    gap: '0.25rem',
  },
  filterButton: {
    fontSize: '0.8125rem',
    whiteSpace: 'nowrap',
    textTransform: 'none',
    padding: '0.375rem 0.75rem',
  },
  pageSizeLabel: {
    fontSize: '0.8125rem',
    color: GRAY_500,
    whiteSpace: 'nowrap',
  },
  pageSizeSelect: {
    marginLeft: '0.375rem',
    fontSize: '0.8125rem',
    padding: '0.25rem 0.375rem',
  },
};

export default FdaSourceFilterControls;
